'use client'

import { MediaType } from '@lastbrand/storefront-proto-registry/api/v1/enums'

import { RichText } from '@src/quince-components/src'
import { Image } from '@src/quince-components/src/atoms/image'
import RichTextDomain from '@src/quince-site-common/src/domains/RichText'

import { getSafeHref, toRichText } from './widget-utils'

import type { Media } from '@lastbrand/storefront-proto-registry/api/v1/widget'
import type { MouseEvent } from 'react'

/**
 * Shared presentation primitives for the PL content widgets (PRODUCT_COMPARISON_CAROUSEL,
 * MEDIA_TEXT_MOSAIC, FEATURE_CARDS, TRUST_BADGES) on the **non-DS (legacy) path**. Styling is owned
 * by each widget's co-located `*.module.scss`; these helpers only normalize proto data into the
 * legacy renderers (`RichText` molecule) and plain media tags. The DS (Panda) path is a separate
 * effort owned by another team.
 */

/** Renders a proto `Media` as `<img>` (IMAGE / unspecified) or `<video>` (VIDEO). `null` when no url.
 *
 *  `squareCrop` omits the asset's intrinsic dimensions for raster images so the legacy `Image` atom
 *  falls back to a 1:1 cover box (CDN `w=h` center-crop) — matching non-PL sections that render
 *  `<Image>` without dims (e.g. `rowOfImageTextBlocks`), where every card crops square regardless of
 *  the source asset's aspect ratio. */
export function WidgetMedia({
  media,
  className,
  loading = 'lazy',
  squareCrop = false,
}: Readonly<{ media?: Media; className?: string; loading?: 'lazy' | 'eager'; squareCrop?: boolean }>) {
  const content = media?.content
  if (!content?.url) return null

  // Proto media URLs are protocol-relative (`//images.ctfassets.net/...`). The legacy Image atom's
  // CDN transform / `getGatsbyFluidImageProps` expects a fully-qualified URL, so prepend the scheme —
  // matching the non-PL flow (`getCardProps` does `https:${url}`). Without this, the optimized path
  // can resolve to an empty src and render nothing (notably for SVG icons).
  const url = content.url.startsWith('//') ? `https:${content.url}` : content.url

  if (media?.type === MediaType.VIDEO) {
    return (
      <video className={className} src={url} muted playsInline loop autoPlay aria-label={content.alt ?? undefined} />
    )
  }

  // SVG icons/glyphs must not go through the Image atom's raster CDN pipeline (resize/quality params
  // break SVGs through the image CDN, so they render nothing). Render them as a plain <img>.
  const isSvg = /\.svg(\?|#|$)/i.test(url)
  if (isSvg) {
    return (
      <img
        className={className}
        src={url}
        alt={content.alt ?? ''}
        loading={loading}
        width={content.width ? content.width : undefined}
        height={content.height ? content.height : undefined}
      />
    )
  }

  // Raster images render through the legacy `Image` atom (Contentful CDN transform / quality / lazy),
  // matching the non-PL flow's `Image.makeImageProps` + `<Image>` rendering rather than a raw <img>.
  return (
    <Image
      url={url}
      title={content.alt ?? ''}
      alt={content.alt ?? ''}
      className={className}
      loading={loading}
      // Drop proto's default `0` (means "no dimension") so we don't emit width="0"/height="0".
      // With `squareCrop`, omit dims entirely → the atom's 1:1 fallback square-crops via the CDN.
      width={!squareCrop && content.width ? content.width : undefined}
      height={!squareCrop && content.height ? content.height : undefined}
    />
  )
}

/** Normalizes a proto rich-text field via `toRichText` and renders it through the legacy RichText
 *  molecule; `null` for empty/absent docs. */
export function WidgetRichText({
  data,
  className,
}: Readonly<{ data?: Parameters<typeof toRichText>[0]; className?: string }>) {
  const richTextProps = RichTextDomain.makeRichTextProps(toRichText(data))
  if (!richTextProps?.content?.content.length) return null
  return <RichText {...richTextProps} className={className} />
}

/** Extracts `{ label, url }` from a raw proto button object, tolerating proto/Contentful field names.
 *  Exported for the DS widgets so both paths parse proto buttons identically. */
export function readButton(raw: Record<string, unknown>): { label: string; url?: string } | null {
  const label = typeof raw.label === 'string' ? raw.label : typeof raw.title === 'string' ? raw.title : undefined
  if (!label) return null
  const link = raw.link as Record<string, unknown> | undefined
  // Slug-based Contentful links (product-entry `entry.slug` or plain `slug`) carry no url/navigationPath;
  // resolve them first, mirroring ContentfulLink.getLinkPath precedence (entry.slug → slug → url).
  const entrySlug = (link?.entry as Record<string, unknown> | undefined)?.slug
  const slug = typeof entrySlug === 'string' ? entrySlug : typeof link?.slug === 'string' ? link.slug : undefined
  const url = slug
    ? `/${slug.replace(/^\//, '')}`
    : ((link?.url as string | undefined) ?? (link?.navigationPath as string | undefined))
  return { label, url }
}

/** Renders proto buttons as simple anchors. Telemetry-rich Button domain deferred to a follow-up. */
export function WidgetButtons({
  buttons,
  className,
  linkClassName,
  onLinkClick,
}: Readonly<{
  buttons?: readonly Record<string, unknown>[]
  className?: string
  linkClassName?: string
  /** Per-anchor click hook (receives the resolved safe href); may preventDefault to suppress navigation. */
  onLinkClick?: (event: MouseEvent<HTMLAnchorElement>, href: string) => void
}>) {
  const parsed = (buttons ?? []).map(readButton).filter((b): b is { label: string; url?: string } => b !== null)
  if (parsed.length === 0) return null

  return (
    <div className={className}>
      {parsed.map((button, index) => {
        const key = `${button.label}-${index}`
        const href = getSafeHref(button.url)
        return href ? (
          <a
            key={key}
            className={linkClassName}
            href={href}
            onClick={
              onLinkClick
                ? event => {
                    onLinkClick(event, href)
                  }
                : undefined
            }
          >
            {button.label}
          </a>
        ) : (
          <span key={key} className={linkClassName}>
            {button.label}
          </span>
        )
      })}
    </div>
  )
}
